import { styled } from "@mui/material/styles";
import RestartLineIcon from "remixicon-react/RestartLineIcon";

import { useCallback, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import ProjectSelect from "$forms/ProjectSelect";
import DockyardSelect from "$forms/DockyardSelect";
import DateRangePicker from "$forms/DateRangePicker";
import useJStyles from "$hooks/useJStyles";
import ProductSelect from "$forms/ProductSelect";
import GenericSelect from "$forms/GenericSelect";
import Button from "$components/Button";
import OrderSelect, { OrderItem } from "../../Orders/forms/OrderSelect";
import ReferencementSelect from "$containers/Orders/forms/ReferencementSelect";
import WarehouseSelector from "$components/JSelectors/WarehouseSelector/WarehouseSelector";
import ProviderSelect from "$containers/Receptions/ReceptionList/components/ProviderSelect";
import { CompanyServices, ProductService } from "$services";
import { Loader } from "$components/Layout/Loader";
import { CacheInvalidationAction } from "$utils/cache";
import { theme } from "$utils/theme/bataillog";
import useAuth from "$hooks/useAuth";
import {
  SuborderType,
  SUBORDER_TYPE_LABELS,
  OrderStatus,
  ORDER_STATUS_LABELS,
} from "$types";

type Props = {
  companyId: string;
  filters: Record<string, any>;
  resetFilters: () => void;
  setFilter: (key: string, value: any) => void;
};

// MUI v5 styled components
const FilterContainer = styled("div")({
  width: "100%",
});

const ItemButtonContainer = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  marginBottom: 20,
});

const ListContainer = styled("div")(() => ({
  display: "flex",
  justifyContent: "space-between",
  flexWrap: "wrap",
  columnGap: `${theme.spacing(2)}px`,
  maxWidth: "1200px",
  rowGap: `${theme.spacing(1)}px`,
}));

const Item = styled("div")(() => ({
  width: `calc(20% - ${theme.spacing(4)}px) !important`,
  alignSelf: "flex-end",
}));

function Filters({ companyId, filters, resetFilters, setFilter }: Props) {
  const { jclasses } = useJStyles();
  const { isContributor } = useAuth();
  const [products, setProducts] = useState([]);
  const [referencement, setReferencement] = useState([]);

  const getProducts = useCallback(async (productIds) => {
    const productReference = await Promise.all(
      productIds.map(async (p) => ProductService.getProductById(p))
    );
    setProducts(productReference);
    return productReference;
  }, []);

  const { data: foundCompany, isLoading: isProjectLoading } = useQuery({
    queryKey: [CacheInvalidationAction.GET_COMPANY_BY_ID, companyId],
    queryFn: () => CompanyServices.getCompanyById(companyId),
    enabled: !!companyId,
    staleTime: 60 * 1000,
  });

  if (isProjectLoading) {
    return <Loader />;
  }

  return (
    <FilterContainer>
      <ItemButtonContainer>
        <Button
          onClick={() => {
            resetFilters();
            setReferencement([]);
          }}
          size="large"
          startIcon={<RestartLineIcon />}
          variant="outlined"
        >
          Réinitialiser
        </Button>
      </ItemButtonContainer>
      <ListContainer className={jclasses.jWrapper}>
        <Item>
          <ProjectSelect
            companyId={companyId}
            multiple
            useRouteProjectWarehouse={isContributor()}
            onChange={(projectId) => setFilter("suborders][project", projectId)}
            placeholder={null}
            value={filters["suborders][project"]}
          />
        </Item>
        <Item>
          <DockyardSelect
            companyId={companyId}
            multiple
            minCharsToTrigerSearch={0}
            useRouteDockyardWarehouse={isContributor()}
            onChange={(dockyardId) =>
              setFilter("suborders][dockyard", dockyardId)
            }
            placeholder={null}
            value={filters["suborders][dockyard"] || []}
          />
        </Item>
        <Item>
          <DateRangePicker
            endDate={filters["orderDate][$lte"] as Date}
            label="Date commande"
            onChange={(dates) => {
              setFilter("orderDate][$gte", dates[0]);
              setFilter("orderDate][$lte", dates[1]);
            }}
            startDate={filters["orderDate][$gte"] as Date}
          />
        </Item>
        <Item>
          <OrderSelect
            displayCreateOrderOption={false}
            companies={foundCompany ? [foundCompany] : []}
            label="N° commande"
            onChange={(orders) =>
              setFilter(
                "reference",
                orders.map((o) => o.reference)
              )
            }
            placeholder={null}
            // Fix the value prop to match the expected OrderItem[] type
            value={
              Array.isArray(filters.reference)
                ? (filters.reference.map((ref) => ({
                    reference: ref,
                  })) as OrderItem[])
                : []
            }
            appendToDefaultFilters={{
              "suborders][type][$ne": [SuborderType.STOCK],
            }}
          />
        </Item>
        <Item>
          <ProductSelect
            companyId={companyId}
            label="Référence produit"
            multiple
            onChange={async (productIds) => {
              const productFetch = await getProducts(productIds);
              const productRefs = productFetch.map((p) => p.reference);
              setFilter("suborders][suborderProducts][reference", productRefs);
            }}
            placeholder={null}
            value={products
              .filter((p) =>
                (
                  (filters[
                    "suborders][suborderProducts][reference"
                  ] as Array<string>) || []
                ).includes(p.reference)
              )
              .map((p) => p._id)}
          />
        </Item>
        <Item>
          <ReferencementSelect
            companyId={companyId}
            label="Référencement"
            multiple
            onChange={(event, data) => {
              const receptions = Array.isArray(data)
                ? data
                : data
                  ? [data]
                  : [];
              // Filter orders by the selected reception(s) directly.
              // This matches how other filters work (dockyard, project, etc.)
              // and avoids relying on populated nested receptionReferences shapes.
              const receptionIds = receptions
                .map((r: any) => r?.id)
                .filter((id: any) => id !== undefined && id !== null);

              setFilter(
                "receptionId",
                receptionIds.length > 0 ? receptionIds : undefined
              );
              setReferencement(receptions);
            }}
            placeholder={null}
            value={referencement}
            additionalFilters={{ type: { $notIn: [SuborderType.STOCK] } }}
          />
        </Item>
        <Item>
          <ProviderSelect
            companyId={companyId}
            onChange={(providerId) => setFilter("provider", providerId)}
            placeholder="Sélectionnez un fournisseur"
            value={filters.provider}
            minCharsToTriggerSearch={0}
            label="Fournisseur"
            multiple
            rowsPerPage={20}
          />
        </Item>
        <Item>
          <WarehouseSelector
            parentFilter={companyId}
            parentFilterRequired
            onChange={(warehouseId) => {
              setFilter("warehouse", warehouseId);
            }}
            multiple
            placeholder="Sélectionnez un entrepôt"
            value={filters.warehouse}
            label="Entrepôt"
            hideLabel={false}
            minCharsToTrigerSearch={0}
            rowsPerPage={20}
          />
        </Item>
        <Item>
          <GenericSelect
            label="Type appro"
            multiple
            onChange={(val) => {
              setFilter("suborders][type][$in", val);
            }}
            options={[
              {
                value: SuborderType.DOCKYARD,
                label: SUBORDER_TYPE_LABELS[SuborderType.DOCKYARD],
              },
              {
                value: SuborderType.PROJECT,
                label: SUBORDER_TYPE_LABELS[SuborderType.PROJECT],
              },
            ]}
            value={filters["suborders][type][$in"]}
          />
        </Item>
        <Item>
          <GenericSelect
            label="Statut"
            multiple
            disabled={isContributor()}
            onChange={(val) => {
              setFilter("status", val);
            }}
            options={[
              {
                value: OrderStatus.TO_DELIVERED,
                label: ORDER_STATUS_LABELS[OrderStatus.TO_DELIVERED],
              },
              {
                value: OrderStatus.DELIVERED_INCOMPLETE,
                label: ORDER_STATUS_LABELS[OrderStatus.DELIVERED_INCOMPLETE],
              },
              {
                value: OrderStatus.DELIVERED_OK,
                label: ORDER_STATUS_LABELS[OrderStatus.DELIVERED_OK],
              },
            ]}
            value={
              isContributor()
                ? Array.isArray(filters.status)
                  ? filters.status
                  : [OrderStatus.DELIVERED_OK]
                : filters.status
            }
          />
        </Item>
      </ListContainer>
    </FilterContainer>
  );
}

export default Filters;
